import { api, ApiError } from "../../api";
import { platformLabel } from "../../platforms";
import { errorBanner, loadingBlock } from "../shared/blocks";
import { keepFocus } from "../shared/focus";
import { createLifecycle, isAbort } from "../shared/lifecycle";
import { generationIntent, finishGenerationIntent } from "../shared/generationIntent";
import { IDEMPOTENCY_HEADER, intentFor, publishScope, replaceIntent, resolveIntent, resolveIntentsWhere } from "../shared/intent";
import { h, safeMessage } from "../shared/safe";
import { noticeBlock } from "./bits";
import { contentShell, createGenerateForm, renderContentResults } from "./content";
import type { Actions, Ctx, DeliveryEntry, Notice, PageData, SectionName } from "./context";
import { newViewState } from "./context";
import { summariseHosts } from "./derive";
import { renderOverviewEssentials, renderHeader } from "./facts";
import { httpUrl, parseClientResponse, parseConnections, parseMasterDetail, parseMasters, parseSatellites, parseSites, parseTemplates } from "./model";
import {
  approveUnreadable,
  describeApproveAck,
  describeApproveRefusal,
  describeApproveStatusRead,
  describeGenerateError,
  describeGenerated,
  describeGeneration,
  describePublishAck,
  describePublishRefusal,
  parseApproveAck,
  parsePublishAck,
  publishIntentResolved,
  publishUnreadable,
  type ApproveOutcome,
  type PublishOutcome,
} from "./outcomes";
import { renderConnections } from "./connections";
import { renderDeploy, renderSites } from "./sites";
import { renderSatellites } from "./satellites";
import { renderTemplates } from "./templates";

// The Client Detail page controller. One visit = one `mountClientDetail` call = one lifecycle. It loads the client's
// records (each validated), builds the sections, and runs the actions. Rules it enforces for every action:
//   - single flight: a pending key makes a repeat click a no-op (and the button says it is busy);
//   - nothing paints after the person has navigated away, or from a response that a newer request has superseded;
//   - view state (open accordions, ticked boxes, typed topic, results) survives the refresh an action triggers;
//   - failures appear inline next to the thing that failed, as text, never in a blocking dialog and never as HTML.

type Part = "client" | "sites" | "connections" | "templates" | "satellites" | "masters";

const enc = encodeURIComponent;

export function mountClientDetail(root: HTMLElement, params: Record<string, string>): void {
  const shell = h("div", { class: "qf qf-cd" });
  root.replaceChildren(shell);
  const life = createLifecycle(shell);
  const clientId = typeof params.id === "string" ? params.id : "";
  if (clientId === "") {
    shell.append(errorBanner("This page needs a client. Go back to the Fleet and open one."));
    return;
  }

  const seq: Record<Part, number> = { client: 0, sites: 0, connections: 0, templates: 0, satellites: 0, masters: 0 };
  const controllers = new Map<string, AbortController>();
  /** Per article: the newest Delivery read. An older read that resolves later is discarded, and a newer one aborts it. */
  const deliverySeq = new Map<string, number>();
  const onHash = () => {
    if (shell.isConnected) return;
    controllers.forEach((c) => c.abort());
    window.removeEventListener("hashchange", onHash);
  };
  window.addEventListener("hashchange", onHash);

  const state = newViewState();
  let data: PageData | null = null;
  let ctx: Ctx | null = null;
  let refreshNote: Notice | null = null;

  const body = h("div", { class: "qf-cd-body-root" });
  const noteSlot = h("div", { id: "cd-refresh-note", "aria-live": "polite" });
  shell.append(body);

  // ---- loading ---------------------------------------------------------------------------------------------

  async function loadPart<T>(part: Part, path: string, parse: (b: unknown) => T | null): Promise<{ ok: true; value: T } | { ok: false; message: string; stale: boolean }> {
    const mine = ++seq[part];
    controllers.get(part)?.abort();
    const ctrl = new AbortController();
    controllers.set(part, ctrl);
    try {
      const raw = await api<unknown>(path, { signal: ctrl.signal });
      const stale = !life.alive() || mine !== seq[part];
      if (stale) return { ok: false, message: "superseded", stale: true };
      const value = parse(raw);
      if (value === null) return { ok: false, message: "The server sent a reply this page does not understand.", stale: false };
      return { ok: true, value };
    } catch (e) {
      if (isAbort(e) || !life.alive() || mine !== seq[part]) return { ok: false, message: "superseded", stale: true };
      return { ok: false, message: e instanceof ApiError ? safeMessage(e.message) : "Couldn't reach the server.", stale: false };
    }
  }

  const paths = {
    client: `/v1/clients/${enc(clientId)}`,
    sites: `/v1/sites?clientId=${enc(clientId)}`,
    connections: `/v1/connections?clientId=${enc(clientId)}`,
    masters: `/v1/content/masters?clientId=${enc(clientId)}`,
    templates: "/v1/templates",
    satellites: `/v1/sites/satellites?clientId=${enc(clientId)}`,
  } as const;

  async function initialLoad(): Promise<void> {
    body.replaceChildren(h("div", { class: "topbar" }, h("h1", null, "Loading…")), loadingBlock("Loading client", 6));
    const [c, s, k, m, t, sat] = await Promise.all([
      loadPart("client", paths.client, parseClientResponse),
      loadPart("sites", paths.sites, parseSites),
      loadPart("connections", paths.connections, parseConnections),
      loadPart("masters", paths.masters, parseMasters),
      loadPart("templates", paths.templates, parseTemplates),
      loadPart("satellites", paths.satellites, parseSatellites),
    ]);
    if (!life.alive()) return;
    const core = [
      ["client", c],
      ["sites", s],
      ["connections", k],
    ] as const;
    const failed = core.find(([, r]) => !r.ok && !r.stale);
    if (core.some(([, r]) => !r.ok && r.stale)) return;
    if (failed && !failed[1].ok) {
      body.replaceChildren(h("div", { class: "topbar" }, h("h1", null, "Client")), errorBanner(`The ${failed[0] === "client" ? "client" : failed[0]} could not be loaded: ${failed[1].message}`, { retry: () => void initialLoad() }));
      return;
    }
    if (!c.ok || !s.ok || !k.ok) return;
    const fail = (r: { ok: boolean; message?: string; stale?: boolean }) => (!r.ok && !r.stale ? { message: r.message as string } : undefined);
    data = {
      client: c.value.client,
      warnings: c.value.warnings,
      sites: s.value,
      connections: k.value.connections,
      platforms: k.value.platforms,
      masters: m.ok ? m.value : null,
      templates: t.ok ? t.value : null,
      satellites: sat.ok ? sat.value : null,
      failures: { masters: fail(m), templates: fail(t), satellites: fail(sat) },
    };
    build();
  }

  /** Quietly re-fetches some parts after an action. A failed refresh keeps what is on screen and says so. */
  async function refresh(...parts: Part[]): Promise<void> {
    if (!data) return;
    const results = await Promise.all(
      parts.map(async (p) => {
        switch (p) {
          case "client":
            return [p, await loadPart(p, paths.client, parseClientResponse)] as const;
          case "sites":
            return [p, await loadPart(p, paths.sites, parseSites)] as const;
          case "connections":
            return [p, await loadPart(p, paths.connections, parseConnections)] as const;
          case "masters":
            return [p, await loadPart(p, paths.masters, parseMasters)] as const;
          case "templates":
            return [p, await loadPart(p, paths.templates, parseTemplates)] as const;
          default:
            return [p, await loadPart(p, paths.satellites, parseSatellites)] as const;
        }
      }),
    );
    if (!life.alive() || !data) return;
    const touched = new Set<SectionName>();
    const failures: string[] = [];
    for (const [part, r] of results) {
      if (!r.ok) {
        if (!r.stale) {
          failures.push(part);
          if (part === "masters" || part === "templates" || part === "satellites") {
            // Keep the previous list on screen if there is one; record the failure only when there was nothing.
            if ((data as PageData)[part] === null) (data.failures as Record<string, unknown>)[part] = { message: r.message };
            touched.add(part === "masters" ? "content" : part);
          }
        }
        continue;
      }
      switch (part) {
        case "client": {
          const v = r.value as NonNullable<ReturnType<typeof parseClientResponse>>;
          data.client = v.client;
          data.warnings = v.warnings;
          ["facts", "templates", "satellites", "deploy"].forEach((n) => touched.add(n as SectionName));
          break;
        }
        case "sites":
          data.sites = r.value as PageData["sites"];
          touched.add("sites");
          break;
        case "connections": {
          const v = r.value as NonNullable<ReturnType<typeof parseConnections>>;
          data.connections = v.connections;
          data.platforms = v.platforms;
          ["deploy", "connections", "satellites"].forEach((n) => touched.add(n as SectionName));
          break;
        }
        case "masters":
          data.masters = r.value as PageData["masters"];
          delete data.failures.masters;
          touched.add("content");
          break;
        case "templates":
          data.templates = r.value as PageData["templates"];
          delete data.failures.templates;
          touched.add("templates");
          break;
        default:
          data.satellites = r.value as PageData["satellites"];
          delete data.failures.satellites;
          touched.add("satellites");
      }
    }
    if (parts.includes("masters") && data.masters) settleAfterMasters();
    refreshNote = failures.length ? { tone: "warn", text: `Couldn't refresh ${failures.join(", ")}. What is shown may be out of date.` } : null;
    noteSlot.replaceChildren(...[noticeBlock(refreshNote)].filter((n): n is HTMLElement => n !== null));
    repaint(...touched);
  }

  // ---- building and painting ---------------------------------------------------------------------------------

  const holders = new Map<SectionName, HTMLElement>();
  let generateForm: ReturnType<typeof createGenerateForm> | null = null;
  let contentResults: HTMLElement | null = null;

  const builders: Record<Exclude<SectionName, "content">, (c: Ctx) => Node[]> = {
    deploy: (c) => [renderDeploy(c)],
    facts: (c) => [renderOverviewEssentials(c)],
    templates: (c) => [renderTemplates(c)],
    sites: (c) => [renderSites(c)],
    satellites: (c) => [renderSatellites(c)],
    connections: (c) => [renderConnections(c)],
  };
  const ORDER: SectionName[] = ["deploy", "facts", "templates", "sites", "satellites", "connections", "content"];

  function paintOne(name: SectionName) {
    if (!ctx) return;
    if (name === "content") {
      generateForm?.sync();
      contentResults?.replaceChildren(renderContentResults(ctx));
      return;
    }
    holders.get(name)?.replaceChildren(...builders[name](ctx));
  }

  function repaint(...names: SectionName[]) {
    if (!life.alive() || !ctx) return;
    const which = names.length ? names : ORDER;
    keepFocus(shell, () => which.forEach(paintOne));
  }

  const actions: Actions = {
    deploy: (platform) => deployMain(platform),
    saveTemplate: (pageType, id) => saveTemplate(pageType, id),
    deploySatellite: (id, platforms) => deploySatellite(id, platforms),
    createSatellite: (key) => createSatellite(key),
    generate: (topic) => generate(topic),
    approve: (id, from) => approve(id, from),
    publish: (id, from, mode) => publish(id, from, mode),
    toggleDelivery: (id) => toggleDelivery(id),
    refreshDelivery: (id) => loadDelivery(id, false),
    reloadSection: async (section) => {
      await refresh(section);
    },
    repaint,
  };

  function build() {
    if (!data) return;
    ctx = { clientId, data, state, actions, life };
    generateForm = createGenerateForm(ctx);
    const content = contentShell(generateForm);
    contentResults = content.results;
    const parts: Node[] = [renderHeader(ctx), noteSlot];
    for (const name of ORDER) {
      if (name === "content") {
        content.el.classList.add("cp-overview-section");
        continue;
      }
      const holder = h("div", { class: "qf-cd-holder cp-overview-section", dataset: { section: name } });
      holders.set(name, holder);
    }
    const available = h("section", { class: "cp-cw-stats", "aria-label": "Client workspace summary" },
      h("div", { class: "cp-cw-stat" }, h("strong", null, String(data.client.services.length)), h("span", null, "Services recorded")),
      h("div", { class: "cp-cw-stat" }, h("strong", null, String(data.connections.filter((c) => c.usable).length)), h("span", null, "Connected platforms")),
      h("div", { class: "cp-cw-stat" }, h("strong", null, data.masters === null ? "—" : String(data.masters.length)), h("span", null, "Content records")),
      h("div", { class: "cp-cw-stat" }, h("strong", null, String(data.platforms.length)), h("span", null, "Available platforms")),
    );
    const continueCard = h("section", { class: "card cp-cw-continue", "aria-labelledby": "cp-cw-continue-title" },
      h("h2", { id: "cp-cw-continue-title" }, "Continue working"),
      h("p", { class: "muted" }, "Pick up a client task or open a website workspace."),
      h("div", { class: "cp-cw-continue-actions" },
        h("a", { class: "cp-btn", href: `#/clients/${encodeURIComponent(clientId)}/site` }, "Create a website"),
        h("a", { class: "cp-btn cp-btn--secondary", href: `#/clients/${encodeURIComponent(clientId)}/drip` }, "Plan a drip feed"),
        h("a", { class: "cp-btn cp-btn--secondary", href: `#/clients/${encodeURIComponent(clientId)}/websites` }, "Open website library"),
        h("a", { class: "cp-cw-link", href: `#/clients/${encodeURIComponent(clientId)}/content` }, "Review client content →"),
      ),
    );
    const mainColumn = h("div", { class: "cp-overview-main" },
      continueCard,
      holders.get("sites")!,
      content.el,
      holders.get("templates")!,
      holders.get("satellites")!,
    );
    const sideColumn = h("aside", { class: "cp-overview-aside" },
      holders.get("facts")!,
      holders.get("connections")!,
      holders.get("deploy")!,
    );
    parts.push(available, h("div", { class: "cp-overview-columns" }, mainColumn, sideColumn));
    body.replaceChildren(...parts);
    repaint();
    settleAfterMasters();
  }

  /**
   * After the article list is known: an intention is unresolved only while its article is still approved (a published or
   * failed article has moved on), and approved articles get a quiet Delivery read so the Publish button can say whether
   * something was already submitted (for example by automatic mode or an earlier visit).
   */
  function settleAfterMasters() {
    const masters = data?.masters;
    if (!masters) return;
    const approved = new Set(masters.filter((m) => m.status === "approved").map((m) => m.id));
    resolveIntentsWhere((scope) => {
      const [kind, cid, mid] = scope.split("|");
      return kind === "publish" && cid === clientId && !approved.has(mid);
    });
    for (const m of masters.filter((x) => x.status === "approved").slice(0, 10)) if (!state.delivery.has(m.id)) void loadDelivery(m.id, false, true);
  }

  // ---- actions ---------------------------------------------------------------------------------------------------

  /** Single flight. Returns false (and does nothing) when the same action is already running. */
  async function single(key: string, sections: SectionName[], fn: () => Promise<void>): Promise<void> {
    if (state.pending.has(key)) return;
    state.pending.add(key);
    repaint(...sections);
    try {
      await fn();
    } finally {
      state.pending.delete(key);
      repaint(...sections);
    }
  }

  const failText = (e: unknown, what: string) => (e instanceof ApiError ? `${what}: ${safeMessage(e.message)}` : `${what}: couldn't reach the server.`);

  function siteResultNotice(label: string, res: unknown): Notice {
    const r = typeof res === "object" && res !== null && !Array.isArray(res) ? (res as Record<string, unknown>) : {};
    const warning = typeof r.warning === "string" && r.warning !== "" ? safeMessage(r.warning, 400) : null;
    // A positive acknowledgement is `deployed: true`. Anything else is an uncertain answer, not a success.
    if (r.deployed !== true) {
      return { tone: "warn", text: `The server replied, but not with a confirmation that the deploy to ${label} happened, so it is not known. The Sites table is refreshed from the server; check it before deploying again.`, detail: warning ? [warning] : undefined };
    }
    const url = httpUrl(r.url);
    if (url) return { tone: warning ? "warn" : "ok", text: `Deployed to ${label}.`, detail: warning ? [warning] : undefined, link: { text: url, href: url } };
    return {
      tone: warning ? "warn" : "info",
      text: `Deployed to ${label}, but it hasn't returned a web address yet (for example Azure: check the Portal, paste the Website URL into Connections, then redeploy to see it here).`,
      detail: warning ? [warning] : undefined,
    };
  }

  async function deployMain(platform: string) {
    const label = platformLabel(platform);
    await single(`deploy:${platform}`, ["deploy"], async () => {
      state.deployNotice = { tone: "info", text: `Deploying to ${label}…` };
      repaint("deploy");
      try {
        const res = await api<unknown>("/v1/sites/deploy", { method: "POST", body: JSON.stringify({ clientId, platform }) });
        if (!life.alive()) return;
        state.deployNotice = siteResultNotice(label, res);
        await refresh("sites", "satellites");
      } catch (e) {
        if (!life.alive()) return;
        state.deployNotice = { tone: "bad", text: failText(e, `Deploy to ${label} failed`) };
      }
    });
  }

  async function saveTemplate(pageType: string, templateId: string) {
    await single("template:save", ["templates"], async () => {
      if (!data) return;
      const next: Record<string, string> = { ...data.client.templateSelection };
      if (templateId) next[pageType] = templateId;
      else delete next[pageType];
      state.templateNotice = null;
      try {
        await api(`/v1/clients/${enc(clientId)}`, { method: "PATCH", body: JSON.stringify({ template_selection_json: JSON.stringify(next) }) });
        if (!life.alive() || !data) return;
        data.client.templateSelection = next;
        state.templateNotice = { tone: "ok", text: "Saved. Click Deploy above to rebuild with these templates." };
      } catch (e) {
        if (!life.alive()) return;
        state.templateNotice = { tone: "bad", text: failText(e, "Not saved. The previous choice is still in effect") };
      }
    });
  }

  async function deploySatellite(id: string, wanted: string[]) {
    await single(`satellite:${id}`, ["satellites"], async () => {
      if (!data) return;
      const usable = new Set(summariseHosts(data).usableHosts.map((p) => p.platform));
      const platforms = wanted.filter((p) => usable.has(p));
      if (platforms.length === 0) {
        state.satelliteNotices.set(id, [{ tone: "bad", text: "Pick at least one connected platform first." }]);
        return;
      }
      state.satelliteNotices.set(id, [{ tone: "info", text: `Deploying to ${platforms.length} platform${platforms.length === 1 ? "" : "s"}…` }]);
      repaint("satellites");
      const outcomes = await Promise.all(
        platforms.map(async (platform): Promise<Notice> => {
          const label = platformLabel(platform);
          try {
            const res = await api<unknown>(`/v1/sites/satellites/${enc(id)}/deploy`, { method: "POST", body: JSON.stringify({ platform }) });
            const n = siteResultNotice(label, res);
            return { ...n, text: `${label}: ${n.text}` };
          } catch (e) {
            return { tone: "bad", text: failText(e, `${label} failed`) };
          }
        }),
      );
      if (!life.alive()) return;
      state.satelliteNotices.set(id, outcomes);
      await refresh("satellites", "sites");
    });
  }

  async function createSatellite(serviceKey: string) {
    await single("satellite:create", ["satellites"], async () => {
      state.satelliteCreateNotice = null;
      try {
        await api("/v1/sites/satellites", { method: "POST", body: JSON.stringify({ clientId, serviceKey }) });
        if (!life.alive()) return;
        state.satelliteCreateNotice = { tone: "ok", text: `Created a satellite for ${safeMessage(serviceKey, 120)}.` };
        await refresh("satellites");
      } catch (e) {
        if (!life.alive()) return;
        state.satelliteCreateNotice = { tone: "bad", text: failText(e, "Couldn't create the satellite") };
      }
    });
  }

  async function generate(topicRaw: string) {
    const topic = topicRaw.trim();
    if (topic === "") {
      state.generateNotice = { tone: "bad", text: "Enter a topic first." };
      repaint("content");
      return;
    }
    await single("generate", ["content"], async () => {
      state.generateNotice = { tone: "info", text: "Generating… this can take a while." };
      repaint("content");
      try {
        const scope=`generate|${clientId}|${topic}`;
        const intention={key:generationIntent(scope)};
        const res = await api<unknown>("/v1/content/generate", { method: "POST", headers:{[IDEMPOTENCY_HEADER]:intention.key}, body: JSON.stringify({ clientId, topic, acknowledgePaidGeneration:true }) });
        if (!life.alive()) return;
        const g = describeGenerated(res);
        if (!g) {
          state.generateNotice = { tone: "bad", text: "The server answered, but not with an article this page can show. Check the article list before trying again." };
          return;
        }
        // Read the returned status and campaign refusal; generation alone grants no publication authority.
        const out = describeGeneration(g);
        finishGenerationIntent(scope);
        state.generated = g;
        state.generatedApproval = null;
        state.generatedPublish = null;
        state.generatedLine = out.line;
        state.generateNotice = out.notice;
        if (out.autoAccepted) state.autoAccepted.add(g.masterId);
        if (out.readDelivery) state.deliveryOpen.add(g.masterId);
        await refresh("masters");
        if (out.readDelivery && life.alive()) await loadDelivery(g.masterId, true);
      } catch (e) {
        if (!life.alive()) return;
        state.generateNotice = { tone: "bad", text: describeGenerateError(e) };
      }
    });
  }

  /** Records an approval result where it was asked for, and keeps the preview (same article) in step. */
  function recordApproval(id: string, from: "list" | "preview", out: ApproveOutcome, status?: string) {
    if (from === "preview") state.generatedApproval = out;
    else state.approvals.set(id, out);
    if (state.generated?.masterId === id) {
      if (from === "list") state.generatedApproval = out;
      if (out.kind === "ok") {
        state.generated.status = status === "published" ? "published" : "approved";
        state.generatedLine = "Approved. It has not been published: use Publish to send it to destinations.";
        state.generateNotice = null; // the earlier "waiting for review" notice is no longer true
      }
    }
  }

  async function approve(id: string, from: "list" | "preview") {
    await single(`approve:${id}`, ["content"], async () => {
      let out: ApproveOutcome;
      let status: string | undefined;
      try {
        const res = await api<unknown>(`/v1/content/masters/${enc(id)}/approve`, { method: "POST" });
        if (!life.alive()) return;
        const ack = parseApproveAck(res);
        out = ack ? describeApproveAck(ack) : approveUnreadable();
        status = ack?.status;
      } catch (e) {
        if (!life.alive()) return;
        out = describeApproveRefusal(e);
      }
      recordApproval(id, from, out, status);
      repaint("content");
      await refresh("masters");
      if (out.kind === "uncertain" && life.alive()) {
        // Not a success and not a refusal: the article's own status settles it.
        const now = data?.masters?.find((m) => m.id === id)?.status ?? null;
        const settled = describeApproveStatusRead(now);
        recordApproval(id, from, settled, now ?? undefined);
      }
    });
  }

  async function publish(id: string, from: "list" | "preview", mode: "default" | "new" = "default") {
    await single(`publish:${id}`, ["content"], async () => {
      const scope = publishScope(clientId, id);
      // ONE intention, one key: reused only when an earlier attempt of THIS publish ended without a readable result;
      // a deliberate new publication ("new", or after a finished one) gets a new key.
      const intent = mode === "new" ? replaceIntent(scope) : intentFor(scope);
      state.approvals.delete(id);
      let outcome: PublishOutcome;
      try {
        const published=data?.masters?.some(m=>m.id===id && m.status==="published");
        const res = await api<unknown>(`/v1/content/masters/${enc(id)}/${enc(published ? "republish" : "publish")}`, { method: "POST", headers: { [IDEMPOTENCY_HEADER]: intent.key }, ...(published ? {body:JSON.stringify({confirmRepublish:true})} : {}) });
        const ack = parsePublishAck(res);
        outcome = ack ? describePublishAck(ack) : publishUnreadable();
      } catch (e) {
        outcome = describePublishRefusal(e);
      }
      if (!life.alive()) return;
      if (publishIntentResolved(outcome)) resolveIntent(scope);
      if (from === "preview") state.generatedPublish = outcome;
      else state.publishes.set(id, outcome);
      if (state.generated?.masterId === id && from === "list") state.generatedPublish = outcome;
      if (state.generated?.masterId === id) {
        state.generateNotice = null; // the earlier generation notice described a state that no longer holds
        if (outcome.kind === "accepted") state.generatedLine = "Submitted for delivery: queued, not delivered. See Delivery for what destinations confirm.";
        if (outcome.kind === "uncertain") state.generatedLine = "A publish attempt has no confirmed result. See Delivery for what the server recorded.";
      }
      const readDelivery = outcome.kind !== "refused";
      if (readDelivery) state.deliveryOpen.add(id);
      repaint("content");
      // The post-mutation read always runs and supersedes any read that was in flight before the publish.
      await Promise.all([refresh("masters"), readDelivery ? loadDelivery(id, true) : Promise.resolve()]);
    });
  }

  async function toggleDelivery(id: string) {
    if (state.deliveryOpen.has(id)) {
      state.deliveryOpen.delete(id);
      repaint("content");
      return;
    }
    state.deliveryOpen.add(id);
    await loadDelivery(id, false);
  }

  /**
   * Reads one article's delivery record. `force` is for reads that follow a change (publish, automatic generation): it
   * supersedes any read already in flight, aborting it, and a superseded read can never paint or release the busy state.
   * A plain refresh (the button, opening the panel) stays single-flight. `quiet` reads only feed the Publish button.
   */
  async function loadDelivery(id: string, force: boolean, quiet = false) {
    const key = `delivery-load:${id}`;
    if (!force && state.pending.has(key)) return;
    const mine = (deliverySeq.get(id) ?? 0) + 1;
    deliverySeq.set(id, mine);
    controllers.get(`delivery:${id}`)?.abort();
    const ctrl = new AbortController();
    controllers.set(`delivery:${id}`, ctrl);
    state.pending.add(key);
    state.delivery.set(id, { state: "loading", quiet });
    repaint("content");
    let entry: DeliveryEntry;
    try {
      const raw = await api<unknown>(`/v1/content/masters/${enc(id)}`, { signal: ctrl.signal });
      const parsed = parseMasterDetail(raw);
      entry = parsed ? { state: "ready", quiet, editorial: parsed.status, delivery: parsed.delivery } : { state: "error", quiet, message: "The server sent delivery information this page does not understand." };
    } catch (e) {
      if (isAbort(e)) return; // superseded by a newer read, or the person left: the newer read owns the state
      entry = { state: "error", quiet, message: e instanceof ApiError ? safeMessage(e.message) : "Couldn't reach the server." };
    }
    if (mine !== deliverySeq.get(id) || !life.alive()) return; // stale: an older read must not overwrite newer state
    state.pending.delete(key);
    state.delivery.set(id, entry);
    repaint("content");
  }

  void initialLoad();
}
